import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLabels, useRequestData } from 'wsm-common-data';
import NestedFacetList from './NestedFacetList';
import NestedFacetPanel from './NestedFacetPanel';

// Transition duration in ms. Used only for the fallback timeout in focus
// management. The actual CSS duration comes from Kinetic utility classes
// `transition-duration-md` (200ms, mobile) or `transition-duration-lg`
// (300ms, desktop), and reduced-motion is handled by Kinetic's global
// @media (prefers-reduced-motion: reduce) rule.
const TRANSITION_DURATION = 300;

// Helper: multi-group items carry groupId directly (matching the props
// accordion passes to MultiFacetGroup); single-facet items use facet.id.
const getFacetGroupId = (fg) => fg.groupId || fg.facet.id;

const NestedFacetNavigation = ({ facetGroups }) => {
	const [activeFacetId, setActiveFacetId] = useState(null);
	const [isTransitioning, setIsTransitioning] = useState(false);
	// Defer aria-live content until first transition to avoid initial-render announcement
	const [announcement, setAnnouncement] = useState('');
	const containerRef = useRef(null);
	const backLinkRef = useRef(null);
	const activeFacetTitleRef = useRef(null);
	const listContainerRef = useRef(null);
	const panelContainerRef = useRef(null);
	const labels = useLabels();
	const { deviceType } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';
	const durationClass = isDesktop
		? 'transition-duration-lg'
		: 'transition-duration-md';

	// Derive the active facet group from fresh props on every render,
	// so filter selections are never stale (mirrors how the accordion
	// path always passes fresh facet data to FacetGroup).
	const activeFacet = activeFacetId
		? facetGroups.find((fg) => getFacetGroupId(fg) === activeFacetId) ||
		  null
		: null;

	const allFiltersLabel = labels.get('ALL_FILTERS');

	const handleSelectFacet = useCallback(
		(facetGroup) => {
			const id = getFacetGroupId(facetGroup);
			activeFacetTitleRef.current = id;
			setIsTransitioning(true);
			setActiveFacetId(id);
			// Update announcement on transition, not on initial render
			setAnnouncement(
				`${
					facetGroup.groupTitleLabel
						? labels.get(facetGroup.groupTitleLabel)
						: facetGroup.facet.label
				} ${labels.get('FILTERS')}`
			);
		},
		[labels]
	);

	const handleBackToList = useCallback(() => {
		setIsTransitioning(true);
		setActiveFacetId(null);
		setAnnouncement(allFiltersLabel);
	}, [allFiltersLabel]);

	// Scope Escape handler to containerRef instead of document
	useEffect(() => {
		if (!activeFacet) {
			return undefined;
		}
		const container = containerRef.current;
		if (!container) {
			return undefined;
		}
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				handleBackToList();
			}
		};
		container.addEventListener('keydown', handleKeyDown);
		return () => container.removeEventListener('keydown', handleKeyDown);
	}, [activeFacet, handleBackToList]);

	// Focus management using transitionend event with fallback timeout
	useEffect(() => {
		if (!isTransitioning) {
			return undefined;
		}

		// Determine which container is transitioning into view
		const transitionTarget = activeFacet
			? panelContainerRef.current
			: listContainerRef.current;

		let settled = false;

		const moveFocus = () => {
			if (settled) {
				return;
			}
			settled = true;
			setIsTransitioning(false);

			if (activeFacet && backLinkRef.current) {
				backLinkRef.current.focus();
			} else if (
				!activeFacet &&
				activeFacetTitleRef.current &&
				containerRef.current
			) {
				const facetButton = containerRef.current.querySelector(
					`[data-facet-id="${activeFacetTitleRef.current}"]`
				);
				if (facetButton) {
					facetButton.focus();
				}
			}
		};

		// Listen for transitionend on the container sliding into view
		if (transitionTarget) {
			transitionTarget.addEventListener('transitionend', moveFocus, {
				once: true
			});
		}

		// Fallback timeout in case transitionend doesn't fire
		// (e.g., element removed mid-transition, display:none applied)
		const fallbackTimer = setTimeout(moveFocus, TRANSITION_DURATION + 50);

		return () => {
			settled = true;
			if (transitionTarget) {
				transitionTarget.removeEventListener(
					'transitionend',
					moveFocus
				);
			}
			clearTimeout(fallbackTimer);
		};
	}, [activeFacet, isTransitioning]);

	const showList = !activeFacet;
	const showPanel = !!activeFacet;

	// Kinetic utility classes for each panel state
	const activeClasses =
		'translate-0 opacity-100 transition-timing-function-decelerate';
	const offscreenLeftClasses =
		'absolute-top absolute-left w-100 translate-left-100 opacity-0 pointer-events-none transition-timing-function-sharp';
	const offscreenRightClasses =
		'absolute-top absolute-left w-100 translate-right-100 opacity-0 pointer-events-none transition-timing-function-sharp';

	return (
		<div
			className="nested-facet-navigation"
			ref={containerRef}
			role="group"
			aria-label={allFiltersLabel}
		>
			{/* Empty on initial render, populated after first transition */}
			<div aria-live="polite" aria-atomic="true" className="sr-only">
				{announcement}
			</div>
			<div className="nested-facet-panels position-relative">
				<div
					ref={listContainerRef}
					className={`nested-facet-container ${durationClass} ${
						showList ? activeClasses : offscreenLeftClasses
					}`}
					aria-hidden={!showList}
				>
					{showList && (
						<NestedFacetList
							facetGroups={facetGroups}
							onSelectFacet={handleSelectFacet}
						/>
					)}
				</div>
				<div
					ref={panelContainerRef}
					className={`nested-facet-container ${durationClass} ${
						showPanel ? activeClasses : offscreenRightClasses
					}`}
					aria-hidden={!showPanel}
				>
					{showPanel && (
						<NestedFacetPanel
							facetGroup={activeFacet}
							allFiltersLabel={allFiltersLabel}
							onBack={handleBackToList}
							backLinkRef={backLinkRef}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

NestedFacetNavigation.propTypes = {
	facetGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

export default NestedFacetNavigation;
